import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { axios } from 'services/axios';
import setCompanyDataStorageLocation from 'API/Company/setCompanyDataStorageLocation';
import {
    handleBillingNotesCRUD,
    getBillingNotesByMode,
} from '../utils/billingNotesHandler';
import { FORM_FIELD_PATHS } from '../constants';

const useUpdateCompany = (
    formData,
    resetToMainTable,
    deletedBillingNoteIDs,
    organisationMode,
    initialFormData,
) => {
    const queryClient = useQueryClient();
    const { restError } = useSelector((state) => state.modal);

    return useMutation({
        mutationFn: async () => {
            const response = await axios.put('/company', formData);

            // The company PUT does not persist dataStorageLocationID — it
            // must be set once via its own dedicated, write-once endpoint.
            const newLocationID =
                formData?.[FORM_FIELD_PATHS.DATA_STORAGE_LOCATION_ID];
            const hadLocationID =
                initialFormData?.[FORM_FIELD_PATHS.DATA_STORAGE_LOCATION_ID];
            if (newLocationID && !hadLocationID) {
                await setCompanyDataStorageLocation(formData.id, newLocationID);
            }

            // Update the company first, then handle billing notes CRUD operations
            const initialBillingNotes = getBillingNotesByMode(
                initialFormData,
                organisationMode,
            );
            const billingNotes = getBillingNotesByMode(
                formData,
                organisationMode,
            );

            if (billingNotes?.length || deletedBillingNoteIDs?.length) {
                await handleBillingNotesCRUD(
                    billingNotes,
                    initialBillingNotes,
                    deletedBillingNoteIDs,
                );
            }

            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['companies']);
            queryClient.invalidateQueries(['company', formData.id]);
            if (resetToMainTable) {
                resetToMainTable();
            }
            toast.success('Updated company successfully');
        },
        onError: (error) => {
            if (error?.response?.data?.errors) {
                const errors = error.response.data.errors;
                const errorMessages = Object.values(errors)?.flat().join('\n');
                toast.error(errorMessages);
            } else if (restError) {
                // backend shows single error message
                toast.error(restError);
            } else {
                // Generic error message if no specific validation errors
                const errorMessage =
                    error?.response?.data?.title ||
                    error?.response?.data?.message ||
                    error?.message ||
                    'Failed to update company';
                toast.error(errorMessage);
            }
        },
        staleTime: 0,
    });
};

export default useUpdateCompany;
